import * as HydraNavState from '@customerio/hydra-nav-state';
import * as HydraPlumaState from '@customerio/hydra-pluma-state';
import { isHydraRoute } from '@/services/router/route-ownership';
import { accountStore } from '@/services/auth/store';
import * as HydraSessionState from '@customerio/hydra-session-state';
import { session } from '@/services/session';
import { billing } from '@/services/billing';
import { ensureSignupPublicFlags } from '@/services/signup/public-flags';
import { sentryCaptureException } from '@/utils/sentry';

export default function injectGlobals() {
  // `Session#initialize()` rejects with the underlying error after exhausting
  // its network retries (or for any non-network failure). Surface it to Sentry
  // so we can see auth-bootstrap failures in production.
  //
  // Then, pre-auth only: once the session has settled unauthenticated (a clean
  // 401 *resolves* `initialized` with `isAuthenticated === false`; network
  // exhaustion rejects it, absorbed by the `.catch` above so the chain reaches
  // `.finally`), resolve the signup public flags so the
  // `window.__hydra__.isHydraRoute` bridge below — and the other call-time
  // ownership consumers (`utils/navigation`, `layouts/root/active-route-sync`) —
  // classify `/signup` correctly if an already-loaded embedded app asks before a
  // signup route gate has mounted to fetch them. `ensureSignupPublicFlags()` is
  // single-flight, so this never double-fetches with the gate's own
  // `useSignupPublicFlags()`. Authenticated users bounce out of the funnel, so
  // their `/signup` ownership answer is moot — skip the fetch (and the
  // `cio:anonymous-id` write it triggers) for them. Fire-and-forget: it must
  // never block first paint.
  //
  // Single chain, not two siblings: `.finally` re-throws, so attaching it as a
  // separate branch off `session.initialized` would leak an unhandled rejection
  // on bootstrap network failures.
  session.initialized
    .catch((error) => {
      sentryCaptureException(error);
    })
    .finally(() => {
      if (!session.isAuthenticated.get()) {
        void ensureSignupPublicFlags();
      }
    });

  // Fast path for the common case — no persisted login at all means a plainly
  // anonymous visitor, known synchronously at bootstrap (the store is seeded
  // from localStorage at module load). Start the flags fetch now, in parallel
  // with session bootstrap, rather than serialized behind `/accounts/current`,
  // so ownership can answer `/signup` sooner. The chain above still covers the
  // expired/rejected-session case (a persisted login that fails to
  // authenticate); single-flight makes the overlap a no-op.
  if (HydraSessionState.sessionsStore.get().length === 0) {
    void ensureSignupPublicFlags();
  }

  Object.assign(window, {
    HydraNavState,
    HydraPlumaState,
    HydraSessionState,
    // Expose routing utilities for Ember to check if a URL belongs to Hydra.
    // Ember asks per navigation (always after the account has loaded), so a
    // call-time account snapshot is correct here. Public-flag-gated ownership
    // (`/signup`) additionally needs `signupPublicFlagsStore` resolved; the
    // pre-auth prefetch above guarantees that before any embedded app can ask.
    __hydra__: {
      ...window.__hydra__,
      isHydraRoute: (path: string) => isHydraRoute(path, accountStore.get()),
      session,
      billing,
      // Capability flag: this shell shows its own in-place re-auth modal on
      // session rejection. Embedded apps check it to delegate their 401
      // handling (announceRefreshAuth) instead of opening their own modal.
      reauthModal: true,
    },
  });
}
